import { supabase } from '../lib/supabase';
import { rbacService } from './rbacService';
import { auditTrailService } from './auditTrailService';

export const teamAccessService = {
  // ============================================================================
  // TEAM MEMBERS MANAGEMENT
  // ============================================================================

  async getAllTeamMembers() {
    try {
      // Get all employees with their role assignments
      const { data: employees, error: empError } = await supabase
        ?.from('employees')
        ?.select(`
          id,
          employee_number,
          first_name,
          last_name,
          email,
          department,
          position,
          is_active,
          hire_date,
          created_at
        `)
        ?.order('created_at', { ascending: false });

      if (empError) throw empError;

      // Get role assignments for all users
      const { data: roleAssignments, error: roleError } = await supabase
        ?.from('user_role_assignments')
        ?.select(`
          id,
          user_email,
          role_id,
          department,
          assigned_by,
          assigned_at,
          is_active,
          custom_roles (
            role_name,
            role_description
          )
        `)
        ?.eq('is_active', true);

      if (roleError) throw roleError;

      // Get last login data from department_access_log
      const { data: accessLogs, error: logError } = await supabase
        ?.from('department_access_log')
        ?.select('user_email, accessed_at')
        ?.order('accessed_at', { ascending: false });

      if (logError) throw logError;

      // Create a map of last login times
      const lastLoginMap = {};
      accessLogs?.forEach(log => {
        if (!lastLoginMap?.[log?.user_email]) {
          lastLoginMap[log?.user_email] = log?.accessed_at;
        }
      });

      // Combine data
      const teamMembers = employees?.map(emp => {
        const userRoles = roleAssignments?.filter(ra => ra?.user_email === emp?.email) || [];
        
        return {
          id: emp?.id,
          employeeNumber: emp?.employee_number,
          firstName: emp?.first_name,
          lastName: emp?.last_name,
          fullName: `${emp?.first_name} ${emp?.last_name}`,
          email: emp?.email,
          department: emp?.department,
          position: emp?.position,
          isActive: emp?.is_active,
          hireDate: emp?.hire_date,
          lastLogin: lastLoginMap?.[emp?.email] || null,
          roles: userRoles?.map(ra => ({
            assignmentId: ra?.id,
            roleId: ra?.role_id,
            roleName: ra?.custom_roles?.role_name,
            roleDescription: ra?.custom_roles?.role_description,
            department: ra?.department,
            assignedBy: ra?.assigned_by,
            assignedAt: ra?.assigned_at
          })),
          createdAt: emp?.created_at
        };
      }) || [];

      return teamMembers;
    } catch (error) {
      console.error('Error fetching team members:', error);
      throw error;
    }
  },

  async getTeamMemberById(memberId) {
    try {
      const { data, error } = await supabase
        ?.from('employees')
        ?.select('*')
        ?.eq('id', memberId)
        ?.single();

      if (error) throw error;

      const roles = await rbacService?.getUserRoleAssignments(data?.email);

      return {
        id: data?.id,
        employeeNumber: data?.employee_number,
        firstName: data?.first_name,
        lastName: data?.last_name,
        email: data?.email,
        department: data?.department,
        position: data?.position,
        isActive: data?.is_active,
        roles
      };
    } catch (error) {
      console.error('Error fetching team member:', error);
      throw error;
    }
  },

  async updateTeamMemberStatus(memberId, isActive) {
    try {
      const { data, error } = await supabase
        ?.from('employees')
        ?.update({ is_active: isActive })
        ?.eq('id', memberId)
        ?.select()
        ?.single();

      if (error) throw error;

      // Log the action
      await supabase?.from('audit_logs')?.insert({
        entity_type: 'employee',
        entity_id: memberId,
        action: isActive ? 'activated' : 'deactivated',
        performed_by: data?.email,
        changes: { is_active: isActive }
      });

      return {
        id: data?.id,
        isActive: data?.is_active
      };
    } catch (error) {
      console.error('Error updating team member status:', error);
      throw error;
    }
  },

  // ============================================================================
  // ROLE ASSIGNMENT MANAGEMENT
  // ============================================================================

  async assignRoleToMember(userEmail, roleId, department, assignedBy) {
    try {
      const assignment = await rbacService?.assignRoleToUser({
        userEmail,
        roleId,
        department,
        assignedBy
      });

      // Log the action
      await supabase?.from('audit_logs')?.insert({
        entity_type: 'role_assignment',
        entity_id: assignment?.id,
        action: 'assigned',
        performed_by: assignedBy,
        changes: { userEmail, roleId, department }
      });

      return assignment;
    } catch (error) {
      console.error('Error assigning role:', error);
      throw error;
    }
  },

  async revokeRoleFromMember(assignmentId, revokedBy) {
    try {
      await rbacService?.revokeUserRole(assignmentId);

      // Log the action
      await supabase?.from('audit_logs')?.insert({
        entity_type: 'role_assignment',
        entity_id: assignmentId,
        action: 'revoked',
        performed_by: revokedBy,
        changes: { assignmentId }
      });

      return { success: true };
    } catch (error) {
      console.error('Error revoking role:', error);
      throw error;
    }
  },

  async bulkAssignRoles(assignments, assignedBy) {
    try {
      const results = [];
      
      for (const assignment of assignments) {
        const result = await this.assignRoleToMember(
          assignment?.userEmail,
          assignment?.roleId,
          assignment?.department,
          assignedBy
        );
        results?.push(result);
      }

      return results;
    } catch (error) {
      console.error('Error bulk assigning roles:', error);
      throw error;
    }
  },

  async bulkRevokeRoles(assignmentIds, revokedBy) {
    try {
      const results = [];
      
      for (const assignmentId of assignmentIds) {
        await this.revokeRoleFromMember(assignmentId, revokedBy);
        results?.push({ assignmentId, success: true });
      }

      return results;
    } catch (error) {
      console.error('Error bulk revoking roles:', error);
      throw error;
    }
  },

  // ============================================================================
  // INVITATION MANAGEMENT
  // ============================================================================

  async getPendingInvitations() {
    try {
      // For now, return empty array as invitation system would need additional tables
      // This can be extended with a proper invitation table
      return [];
    } catch (error) {
      console.error('Error fetching invitations:', error);
      throw error;
    }
  },

  async sendInvitation(invitation) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get company settings for sender info
      const { data: companySettings } = await supabase
        ?.from('company_settings')
        ?.select('company_name, id')
        ?.eq('user_id', user?.id)
        ?.single();

      if (!companySettings) throw new Error('Company settings not found');

      // Create invitation record in rbac_users table with pending status
      const { data: newUser, error: insertError } = await supabase
        ?.from('rbac_users')
        ?.insert({
          company_id: companySettings?.id,
          email: invitation?.email,
          full_name: invitation?.name || '',
          role_id: invitation?.roleId,
          department: invitation?.department || '',
          is_active: false,
          invitation_sent_at: new Date()?.toISOString(),
          invitation_status: 'pending'
        })
        ?.select()
        ?.single();

      if (insertError) throw insertError;

      // Send invitation email using Resend
      const invitationLink = `${window.location?.origin}/accept-invitation?token=${newUser?.id}`;
      
      const emailHtml = `
        <!DOCTYPE html>
        <html>
          <head>
            <style>
              body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
              .container { max-width: 600px; margin: 0 auto; padding: 20px; }
              .header { background: linear-gradient(135deg, #16a34a 0%, #15803d 100%); color: white; padding: 30px; text-align: center; border-radius: 8px 8px 0 0; }
              .content { background: #f9fafb; padding: 30px; border-radius: 0 0 8px 8px; }
              .button { display: inline-block; background: #16a34a; color: white; padding: 12px 30px; text-decoration: none; border-radius: 6px; margin: 20px 0; }
              .footer { text-align: center; margin-top: 20px; color: #6b7280; font-size: 12px; }
            </style>
          </head>
          <body>
            <div class="container">
              <div class="header">
                <h1>Team Invitation</h1>
              </div>
              <div class="content">
                <p>Hello ${invitation?.name || 'there'},</p>
                <p>You've been invited to join <strong>${companySettings?.company_name}</strong> on the PNG HR Compliance Platform.</p>
                <p><strong>Role:</strong> ${invitation?.roleName || 'Team Member'}</p>
                ${invitation?.department ? `<p><strong>Department:</strong> ${invitation?.department}</p>` : ''}
                <p>Click the button below to accept your invitation and set up your account:</p>
                <a href="${invitationLink}" class="button">Accept Invitation</a>
                <p style="color: #6b7280; font-size: 14px;">Or copy this link: ${invitationLink}</p>
              </div>
              <div class="footer">
                <p>This invitation was sent by ${companySettings?.company_name}</p>
                <p>&copy; ${new Date()?.getFullYear()} Pacific RBS Ltd - PNG HR Compliance Platform</p>
              </div>
            </div>
          </body>
        </html>
      `;

      // Call Resend edge function
      const { error: emailError } = await supabase?.functions?.invoke('send-team-invitation', {
        body: {
          to: invitation?.email,
          subject: `Invitation to join ${companySettings?.company_name} - PNG HR Platform`,
          html: emailHtml
        }
      });

      if (emailError) {
        console.error('Email sending failed:', emailError);
        // Don't throw - invitation record is created, email can be resent
      }

      return { 
        success: true, 
        message: 'Invitation sent successfully',
        userId: newUser?.id
      };
    } catch (error) {
      console.error('Error sending invitation:', error);
      throw error;
    }
  },

  // ============================================================================
  // ACTIVITY TRACKING & COMPLIANCE AUDITS
  // ============================================================================

  async getUserActivityLog(filters = {}) {
    try {
      const { userEmail, department, module, dateFrom, dateTo, limit = 100 } = filters;

      // Get department access logs
      let query = supabase
        ?.from('department_access_log')
        ?.select('*')
        ?.order('accessed_at', { ascending: false })
        ?.limit(limit);

      if (userEmail) {
        query = query?.eq('user_email', userEmail);
      }

      if (department) {
        query = query?.eq('department', department);
      }

      if (module) {
        query = query?.eq('module', module);
      }

      if (dateFrom) {
        query = query?.gte('accessed_at', dateFrom);
      }

      if (dateTo) {
        query = query?.lte('accessed_at', dateTo);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(log => ({
        id: log?.id,
        userEmail: log?.user_email,
        department: log?.department,
        module: log?.module,
        action: log?.action,
        accessGranted: log?.access_granted,
        denialReason: log?.denial_reason,
        accessedAt: log?.accessed_at
      })) || [];
    } catch (error) {
      console.error('Error fetching user activity log:', error);
      throw error;
    }
  },

  async getComplianceAuditTrail(filters = {}) {
    try {
      // Use existing audit trail service for comprehensive audit data
      const auditData = await auditTrailService?.getAllAuditEntries({
        ...filters,
        limit: filters?.limit || 100
      });

      return auditData;
    } catch (error) {
      console.error('Error fetching compliance audit trail:', error);
      throw error;
    }
  },

  async getAccessStatistics() {
    try {
      // Get access statistics from department_access_log
      const { data: accessLogs, error } = await supabase
        ?.from('department_access_log')
        ?.select('user_email, access_granted, accessed_at');

      if (error) throw error;

      const totalAccess = accessLogs?.length || 0;
      const grantedAccess = accessLogs?.filter(log => log?.access_granted)?.length || 0;
      const deniedAccess = totalAccess - grantedAccess;

      // Get unique users
      const uniqueUsers = new Set(accessLogs?.map(log => log?.user_email))?.size || 0;

      // Get access in last 24 hours
      const yesterday = new Date();
      yesterday?.setDate(yesterday?.getDate() - 1);
      const recentAccess = accessLogs?.filter(log => 
        new Date(log?.accessed_at) > yesterday
      )?.length || 0;

      return {
        totalAccess,
        grantedAccess,
        deniedAccess,
        uniqueUsers,
        recentAccess,
        accessGrantRate: totalAccess > 0 ? ((grantedAccess / totalAccess) * 100)?.toFixed(1) : 0
      };
    } catch (error) {
      console.error('Error fetching access statistics:', error);
      throw error;
    }
  },

  async getFailedLoginAttempts(hours = 24) {
    try {
      const since = new Date();
      since?.setHours(since?.getHours() - hours);

      const { data, error } = await supabase
        ?.from('department_access_log')
        ?.select('*')
        ?.eq('access_granted', false)
        ?.gte('accessed_at', since?.toISOString())
        ?.order('accessed_at', { ascending: false });

      if (error) throw error;

      return data?.map(log => ({
        userEmail: log?.user_email,
        department: log?.department,
        module: log?.module,
        denialReason: log?.denial_reason,
        attemptedAt: log?.accessed_at
      })) || [];
    } catch (error) {
      console.error('Error fetching failed login attempts:', error);
      throw error;
    }
  },

  // ============================================================================
  // ROLE DISTRIBUTION & STATISTICS
  // ============================================================================

  async getRoleDistribution() {
    try {
      const { data, error } = await supabase
        ?.from('user_role_assignments')
        ?.select(`
          role_id,
          custom_roles (
            role_name
          )
        `)
        ?.eq('is_active', true);

      if (error) throw error;

      // Count roles
      const roleCount = {};
      data?.forEach(assignment => {
        const roleName = assignment?.custom_roles?.role_name || 'Unknown';
        roleCount[roleName] = (roleCount?.[roleName] || 0) + 1;
      });

      return Object.entries(roleCount)?.map(([roleName, count]) => ({
        roleName,
        count
      }));
    } catch (error) {
      console.error('Error fetching role distribution:', error);
      throw error;
    }
  },

  async getDepartmentAccessMatrix() {
    try {
      const { data, error } = await supabase
        ?.from('user_role_assignments')
        ?.select('user_email, department, custom_roles(role_name)')
        ?.eq('is_active', true);

      if (error) throw error;

      // Group by department
      const departmentMatrix = {};
      data?.forEach(assignment => {
        const dept = assignment?.department || 'Unassigned';
        if (!departmentMatrix?.[dept]) {
          departmentMatrix[dept] = {
            department: dept,
            users: new Set(),
            roles: {}
          };
        }
        departmentMatrix?.[dept]?.users?.add(assignment?.user_email);
        const roleName = assignment?.custom_roles?.role_name || 'Unknown';
        departmentMatrix[dept].roles[roleName] = (departmentMatrix?.[dept]?.roles?.[roleName] || 0) + 1;
      });

      return Object.values(departmentMatrix)?.map(dept => ({
        department: dept?.department,
        userCount: dept?.users?.size,
        roles: dept?.roles
      }));
    } catch (error) {
      console.error('Error fetching department access matrix:', error);
      throw error;
    }
  }
};

export default teamAccessService;